// frontend/src/App.js
import React, { useState, useEffect } from 'react';
import { LogOut, Eye, EyeOff, Menu as MenuIcon, ShoppingCart, Users } from 'lucide-react';
import axios from 'axios';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Menu from './components/Menu';
import Orders from './components/Orders';
import UserManagement from './components/UserManagement';
import ForcePasswordChange from './components/ForcePasswordChange';
import { buttonPrimary, navActive, navInactive, focusRing } from './theme';

const API_URL = '/api';

const App = () => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem('refreshToken'));
  const [currentPage, setCurrentPage] = useState('menu');
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Check URL path on mount and when it changes
  useEffect(() => {
    const checkPath = () => {
      const path = window.location.pathname;
      if (path === '/admin/users') {
        setCurrentPage('users');
      }
    };
    
    checkPath();
    
    // Listen for URL changes (browser back/forward)
    window.addEventListener('popstate', checkPath);
    return () => window.removeEventListener('popstate', checkPath);
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const userData = localStorage.getItem('user');
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setUser(parsedUser);
          setMustChangePassword(parsedUser.mustChangePassword || false);
        } catch (e) {
          console.error('Error parsing user data:', e);
          handleLogout();
        }
      }
    }
  }, [token]);

  const LoginForm = () => {
    const [credentials, setCredentials] = useState({ username: '', password: '', rememberMe: false });
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleLogin = async () => {
      if (!credentials.username || !credentials.password) {
        setError('Please enter username and password');
        return;
      }

      try {
        setError('');
        setIsLoading(true);
        const response = await axios.post(`${API_URL}/auth/login`, credentials);
        
        const { accessToken, refreshToken: newRefreshToken, user: userData } = response.data;
        
        localStorage.setItem('token', accessToken);
        localStorage.setItem('user', JSON.stringify(userData));
        
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken);
          setRefreshToken(newRefreshToken);
        }
        
        setToken(accessToken);
        setUser(userData);
      } catch (err) {
        console.error('Login error:', err);
        setError(err.response?.data?.message || 'Login failed. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        handleLogin();
      }
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-md">
          <h1 className="text-3xl font-bold text-gray-800 mb-6 text-center">Restaurant Manager</h1>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
              <input
                type="text"
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                onKeyPress={handleKeyPress}
                className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={credentials.password}
                  onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                  onKeyPress={handleKeyPress}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${focusRing}`}
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-2.5 text-gray-500"
                  disabled={isLoading}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <div className="flex items-center mb-4">
              <input
                type="checkbox"
                id="rememberMe"
                checked={credentials.rememberMe}
                onChange={(e) => setCredentials({...credentials, rememberMe: e.target.checked})}
                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="rememberMe" className="ml-2 text-sm text-gray-700">
                Remember me for 30 days
              </label>
            </div>
            <button
              onClick={handleLogin}
              disabled={isLoading}
              className={`w-full py-2 rounded-md transition-colors disabled:bg-gray-400 ${buttonPrimary}`}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </button>
            <p className="text-xs text-gray-500 text-center mt-4">
              Demo: admin/admin123 or user/user123
            </p>
          </div>
        </div>
      </div>
    );
  };

  const handleLogout = async () => {
    try {
      const storedRefreshToken = localStorage.getItem('refreshToken');
      if (storedRefreshToken) {
        await axios.post(`${API_URL}/auth/logout`, { refreshToken: storedRefreshToken });
      }
    } catch (err) {
      console.error('Logout error:', err);
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setCurrentPage('menu');
  };

  const handleNavigate = (page) => {
    setCurrentPage(page);
    // Update URL without page reload
    if (page === 'users') {
      window.history.pushState({}, '', '/admin/users');
    } else {
      window.history.pushState({}, '', '/');
    }
  };

  if (!user || !token) {
    return <LoginForm />;
  }

  // Check if user must change password
  if (mustChangePassword) {
    return <ForcePasswordChange onPasswordChanged={() => {
      // Update user object to remove must change password flag
      const updatedUser = { ...user, mustChangePassword: false };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setMustChangePassword(false);
    }} />;
  }

  // Render the appropriate component based on currentPage
  const renderPage = () => {
    switch (currentPage) {
      case 'menu':
        return <Menu user={user} />;
      case 'orders':
        return <Orders user={user} />;
      case 'users':
        return <UserManagement />;
      default:
        return <Menu user={user} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <nav className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold text-gray-800">Restaurant Manager</h1>
            <div className="flex items-center gap-6">
              <div className="flex gap-4">
                <button
                  onClick={() => handleNavigate('menu')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md ${
                    currentPage === 'menu' ? navActive : navInactive
                  }`}
                >
                  <MenuIcon size={18} />
                  Menu
                </button>
                <button
                  onClick={() => handleNavigate('orders')}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md ${
                    currentPage === 'orders' ? navActive : navInactive
                  }`}
                >
                  <ShoppingCart size={18} />
                  Orders
                </button>
                {user.role === 'admin' && (
                  <button
                    onClick={() => handleNavigate('users')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md ${
                      currentPage === 'users' ? navActive : navInactive
                    }`}
                  >
                    <Users size={18} />
                    Users
                  </button>
                )}
              </div>
              <div className="flex items-center gap-4 border-l pl-4">
                <span className="text-sm text-gray-600">
                  {user.username} ({user.role})
                </span>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-md"
                >
                  <LogOut size={18} />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {renderPage()}
    </div>
  );
};

export default App;